import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Dashboard APIs
export const getDashboardSummary = () => api.get('/dashboard/summary');
export const getTenant = () => api.get('/tenant');
export const getConfig = () => api.get('/config');

// License APIs
export const getLicenses = () => api.get('/licenses');
export const getLicensePrices = () => api.get('/licenses/prices');
export const setLicensePrice = (priceData) => api.post('/licenses/prices', priceData);
export const updateLicensePrice = (skuId, price) => api.put(`/licenses/prices/${skuId}`, { monthly_price_per_user: price });
export const deleteLicensePrice = (skuId) => api.delete(`/licenses/prices/${skuId}`);

// Security APIs
export const getSecurityPosture = () => api.get('/security/posture');

// Identity APIs
export const getIdentityStats = () => api.get('/identity');

// Storage APIs
export const getStorage = () => api.get('/storage');

// Azure APIs
export const getAzureSpend = () => api.get('/azure/spend');

// Governance APIs
export const getGovernance = () => api.get('/governance');

// Operations APIs
export const getOperations = (days = 7) => api.get(`/operations?days=${days}`);
export const getTickets = (days = 7) => api.get(`/tickets?days=${days}`);

// Service Health APIs
export const getServicesHealth = () => api.get('/services/health');
export const getInternalServices = () => api.get('/services/internal');
export const addInternalService = (service) => api.post('/services/internal', service);
export const updateInternalService = (id, update) => api.put(`/services/internal/${id}`, update);
export const deleteInternalService = (id) => api.delete(`/services/internal/${id}`);
export const checkInternalService = (id) => api.post(`/services/internal/${id}/check`);
export const checkAllInternalServices = () => api.post('/services/internal/check-all');
export const getServiceUptime = (id, hours = 24) => api.get(`/services/internal/${id}/uptime?hours=${hours}`);
export const getServiceStats = (id) => api.get(`/services/internal/${id}/stats`);
export const getAlertHistory = (limit = 50) => api.get(`/alerts/history?limit=${limit}`);

// Goals APIs
export const getGoals = () => api.get('/goals');
export const createGoal = (goal) => api.post('/goals', goal);
export const updateGoal = (id, goal) => api.put(`/goals/${id}`, goal);
export const deleteGoal = (id) => api.delete(`/goals/${id}`);

// Custom Metrics APIs
export const getCustomMetrics = () => api.get('/metrics/custom');
export const createCustomMetric = (metric) => api.post('/metrics/custom', metric);
export const deleteCustomMetric = (id) => api.delete(`/metrics/custom/${id}`);

// Alert APIs
export const getAlerts = () => api.get('/alerts');
export const createAlert = (alert) => api.post('/alerts', null, { params: alert });

// External Services APIs
export const getExternalServices = () => api.get('/external-services');
export const getSmartsheetLicenses = () => api.get('/external-services/smartsheet');
export const getRingCentralInfo = () => api.get('/external-services/ringcentral');

// EverWatch APIs
export const getEverWatchData = (days = 7) => api.get(`/everwatch?time_range_days=${days}`);

// Jira Satisfaction APIs
export const getJiraSatisfaction = (days = 7) => api.get(`/jira/satisfaction?days=${days}`);

// Project Management APIs
export const getProjects = () => api.get('/projects');
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (project) => api.post('/projects', project);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const addProjectStatus = (projectId, content, userEmail) =>
  api.post(`/projects/${projectId}/status?user_email=${encodeURIComponent(userEmail || '')}`, { content });
export const deleteProjectStatus = (projectId, statusId) =>
  api.delete(`/projects/${projectId}/status/${statusId}`);

// Dashboard Management APIs
export const getUserDashboards = (email) => api.get(`/dashboards?email=${encodeURIComponent(email)}`);
export const getDashboard = (dashboardId) => api.get(`/dashboards/${dashboardId}`);
export const createDashboard = (data, email) => api.post(`/dashboards?email=${encodeURIComponent(email)}`, data);
export const updateDashboard = (dashboardId, data) => api.put(`/dashboards/${dashboardId}`, data);
export const updateDashboardLayout = (dashboardId, layout) => api.put(`/dashboards/${dashboardId}/layout`, { layout });
export const deleteDashboard = (dashboardId) => api.delete(`/dashboards/${dashboardId}`);

// Dashboard Sharing APIs
export const getDashboardShares = (dashboardId, ownerEmail) => 
  api.get(`/dashboards/${dashboardId}/shares?owner_email=${encodeURIComponent(ownerEmail)}`);
export const shareDashboard = (dashboardId, ownerEmail, shareData) => 
  api.post(`/dashboards/${dashboardId}/share?owner_email=${encodeURIComponent(ownerEmail)}`, shareData);
export const unshareDashboard = (dashboardId, ownerEmail, email) => 
  api.delete(`/dashboards/${dashboardId}/share/${encodeURIComponent(email)}?owner_email=${encodeURIComponent(ownerEmail)}`);
export const createShareLink = (dashboardId, ownerEmail, linkData) => 
  api.post(`/dashboards/${dashboardId}/share-link?owner_email=${encodeURIComponent(ownerEmail)}`, linkData);
export const deleteShareLink = (dashboardId, ownerEmail, linkId) => 
  api.delete(`/dashboards/${dashboardId}/share-link/${linkId}?owner_email=${encodeURIComponent(ownerEmail)}`);
export const getSharedDashboardByLink = (linkId) => api.get(`/shared/link/${linkId}`);
export const getDashboardsSharedWithMe = (email) => api.get(`/shared/dashboards?email=${encodeURIComponent(email)}`);

// Azure Cost APIs
export const getAzureCostsAllScopes = (timeRangeDays = 30, accessToken = null) => {
  const config = accessToken ? { headers: { 'Authorization': `Bearer ${accessToken}` } } : {};
  return api.get(`/azure/costs/all-scopes?time_range_days=${timeRangeDays}`, config);
};
export const getAzureSubscriptions = (accessToken = null) => {
  const config = accessToken ? { headers: { 'Authorization': `Bearer ${accessToken}` } } : {};
  return api.get('/azure/subscriptions', config);
};
export const getAzureManagementGroups = (accessToken = null) => {
  const config = accessToken ? { headers: { 'Authorization': `Bearer ${accessToken}` } } : {};
  return api.get('/azure/management-groups', config);
};

export default api;
